import { useState } from "react";

export interface CustomerFormData {
  name: string;
  email: string;
  phone: string;
  notes: string;
}

interface CustomerFormProps {
  onSubmit: (data: CustomerFormData) => void;
  onBack: () => void;
  loading?: boolean;
  notesProminent?: boolean;
}

export function CustomerForm({ onSubmit, onBack, loading, notesProminent = false }: CustomerFormProps) {
  const [form, setForm] = useState<CustomerFormData>({
    name: "",
    email: "",
    phone: "",
    notes: "",
  });
  const [errors, setErrors] = useState<Partial<CustomerFormData>>({});

  function validate(): boolean {
    const errs: Partial<CustomerFormData> = {};
    if (!form.name.trim()) errs.name = "Name is required";
    if (!form.email.trim()) errs.email = "Email is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      errs.email = "Invalid email address";
    if (!form.phone.trim()) errs.phone = "Phone is required";
    if (notesProminent && !form.notes.trim()) errs.notes = "Notes are required";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (validate()) onSubmit(form);
  }

  function inputClass(id: keyof CustomerFormData) {
    return `w-full border rounded-[var(--radius)] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:border-transparent ${
      errors[id] ? "border-red-400 focus:ring-red-300" : "border-gray-300 focus:ring-[var(--accent)]"
    }`;
  }

  const notesField = (
    <div>
      <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-1">
        Notes {notesProminent ? <span className="text-red-500">*</span> : <span className="text-gray-400">(optional)</span>}
      </label>
      <textarea
        id="notes"
        rows={4}
        value={form.notes}
        onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
        placeholder="Anything we should know?"
        className={inputClass("notes")}
      />
      {errors.notes && <p className="mt-1 text-xs text-red-600">{errors.notes}</p>}
    </div>
  );

  const baseFields = (
    <>
      <div>
        <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
          Your name <span className="text-red-500">*</span>
        </label>
        <input
          id="name"
          type="text"
          value={form.name}
          onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
          placeholder="Jane Doe"
          className={inputClass("name")}
        />
        {errors.name && <p className="mt-1 text-xs text-red-600">{errors.name}</p>}
      </div>

      <div>
        <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
          Email address <span className="text-red-500">*</span>
        </label>
        <input
          id="email"
          type="email"
          value={form.email}
          onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
          placeholder="jane@example.com"
          className={inputClass("email")}
        />
        {errors.email && <p className="mt-1 text-xs text-red-600">{errors.email}</p>}
      </div>

      <div>
        <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
          Phone number <span className="text-red-500">*</span>
        </label>
        <input
          id="phone"
          type="tel"
          value={form.phone}
          onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
          placeholder="+1 555 000 0000"
          className={inputClass("phone")}
        />
        {errors.phone && <p className="mt-1 text-xs text-red-600">{errors.phone}</p>}
      </div>
    </>
  );

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {notesProminent ? (
        <>
          {notesField}
          {baseFields}
        </>
      ) : (
        <>
          {baseFields}
          {notesField}
        </>
      )}

      <div className="flex gap-3 pt-2">
        <button
          type="button"
          onClick={onBack}
          className="flex-1 py-2.5 px-4 rounded-[var(--radius)] border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
        >
          Back
        </button>
        <button
          type="submit"
          disabled={loading}
          className="flex-1 py-2.5 px-4 rounded-[var(--radius)] text-white text-sm font-medium disabled:opacity-60 transition-opacity hover:opacity-90"
          style={{ backgroundColor: "var(--accent)" }}
        >
          {loading ? "Please wait..." : "Continue"}
        </button>
      </div>
    </form>
  );
}

// Default export for backward compatibility
export default CustomerForm;
